import * as React from "react";
import clsx from "clsx";
import { CheckOutlined, ExpandOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Popover, theme, Typography } from "antd";
import { SlideDetailsPopoverChip } from "@/components/SlideDetailsPopoverChip";
import { SlidePreviewEnlargeModal, type SlidePreviewSlide } from "@/components/SlidePreviewEnlargeModal";
import { logger } from "@/helpers/tslog";
import { SLIDE_FRAME_KEYS, useContentSlideStore, type ContentSlidePrimaryCompany } from "@/state/contentSlideStore";
import { CompanySearchField } from "../../components/CompanySearchField";
import { PeersCatalogInfoButton } from "../../components/PeersCatalogInfoButton";

const { Title } = Typography;

const SLIDE_FRAME_PRECEDENT_HELP = (
  <p className="m-0 text-sm text-gray-800 leading-relaxed">
    Below are precedent slides created for this analysis. <strong>Select one slide precedent</strong>  you would like us to start from.

The precedent slide guides layout and structure for the slide we will create, while we will update it for the Primary Company selection above, time period of analysis, relative benchmarks and general content around the slide.
  </p>
);

const SLIDE_FRAME_IMAGE_SRC = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
  (n) => `assets/slides/${encodeURIComponent(`Slide${n}.PNG`)}`
);

export type SlidePrecedent = SlidePreviewSlide;

const PRECEDENT_ACTION_CHIP =
  "inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-[#d9dde3] bg-white px-3 text-[13px] font-medium leading-none text-[#1f2328] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:border-[#cdd3db] hover:bg-[#f8f9fb] hover:shadow-[0_2px_6px_rgba(0,0,0,0.10)] active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-1";

const PRECEDENT_ACTION_ICON_WRAP = "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#1f2328]";

export default function SelectPrecedentStep() {
  const primaryCompany = useContentSlideStore((s) => s.primaryCompany);
  const setPrimaryCompany = useContentSlideStore((s) => s.setPrimaryCompany);
  const selectedSlideFrameKey = useContentSlideStore((s) => s.selectedSlideFrameKey);
  const setSelectedSlideFrameKey = useContentSlideStore((s) => s.setSelectedSlideFrameKey);
  const createNewPrecedentIntent = useContentSlideStore((s) => s.createNewPrecedentIntent);
  const setCreateNewPrecedentIntent = useContentSlideStore((s) => s.setCreateNewPrecedentIntent);

  const [enlargePreview, setEnlargePreview] = React.useState<SlidePrecedent | null>(null);
  const [slides] = React.useState<SlidePrecedent[]>(() =>
    SLIDE_FRAME_IMAGE_SRC.map((src, idx) => ({
      id: SLIDE_FRAME_KEYS[idx] ?? idx,
      title: `Slide frame ${idx + 1}`,
      src,
    }))
  );

  const companySelected = !!primaryCompany;

  const handleSelect = React.useCallback(
    (payload: ContentSlidePrimaryCompany) => {
      setPrimaryCompany(payload);
      logger.info("Select Precedent: primary company stored", payload);
    },
    [setPrimaryCompany]
  );

  const { token } = theme.useToken();
  const buttonToken = (token as { Button?: { colorPrimary?: string; colorPrimaryHover?: string } }).Button;
  const primaryColor = buttonToken?.colorPrimary ?? token.colorPrimary;
  const primaryHover = buttonToken?.colorPrimaryHover ?? token.colorPrimaryHover ?? primaryColor;

  const selectedAccentStyle = React.useMemo(
    () =>
      ({
        ["--precedent-primary"]: primaryColor,
        ["--precedent-primary-hover"]: primaryHover,
      }) as React.CSSProperties,
    [primaryColor, primaryHover]
  );

  return (
    <div className="p-4 text-sm text-gray-800">
      <div className="w-[300px] leading-none">
        <div className="leading-normal text-gray-900">
          <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1">
              <CompanySearchField
                displayText={primaryCompany?.companyName || "Search Company Name"}
                onSelectCompany={handleSelect}
                className="w-full"
              />
            </div>
            <PeersCatalogInfoButton />
          </div>
        </div>
      </div>

      {companySelected ? (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-1">
            <Title level={3} className="!mb-0 !text-[1.125rem] !leading-snug font-bold">
            Select a Precedent Slide
            </Title>
            <Popover
              content={SLIDE_FRAME_PRECEDENT_HELP}
              trigger="click"
              overlayStyle={{ maxWidth: 380 }}
            >
              <button
                type="button"
                className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="More information about slide frame precedents"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </Popover>
          </div>
          <p className="text-sm text-gray-600">
          Choose the type of slide you want to build for the selected company.
          </p>

          <div
            className="mt-3 w-full max-h-[61vh] overflow-y-auto overscroll-contain pr-1"
            aria-label="Slide frame precedents"
          >
            <div className="grid w-full grid-cols-2 gap-3 pb-1">
              {SLIDE_FRAME_IMAGE_SRC.map((src, idx) => {
                const frameKey = SLIDE_FRAME_KEYS[idx]!;
                const isSelected = selectedSlideFrameKey === frameKey;
                const detailsContent = (
                  <div className="max-w-xs text-sm text-gray-700">
                    <p className="m-0 font-medium text-gray-900">Slide Details</p>
                    <p className="mb-0 mt-1">
                    The precedent slide guides layout and structure for the slide we will create, while we will update it for the Primary Company selection above, time period of analysis, relative benchmarks and general content around the slide.
                    </p>
                    <div className="mt-2">
                      <span className="inline-flex items-center rounded-md bg-blue-500 px-3 py-2 text-[12px] font-medium leading-none text-white">
                        Share Price Performance
                      </span>
                    </div>
                  </div>
                );
                return (
                  <div
                    key={src}
                    style={isSelected ? selectedAccentStyle : undefined}
                    className={`group relative w-full overflow-hidden rounded-md border-2 transition-[box-shadow,border-color] duration-[160ms] ease-out ${
                      isSelected
                        ? "bg-gray-50 border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)]"
                        : "bg-gray-50 border-gray-200 shadow-sm hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)]"
                    }`}
                  >
                    {isSelected ? (
                      <>
                        {/* Translucent primary over the slide so the image still reads through */}
                        <div
                          className="pointer-events-none absolute inset-0 z-[1] rounded-md"
                          style={{
                            backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
                          }}
                          aria-hidden
                        />
                        {/* Hover: extra 10% darken on top of the green wash */}
                        <div
                          className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                          aria-hidden
                        />
                      </>
                    ) : (
                      <div
                        className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                        aria-hidden
                      />
                    )}
                    {isSelected ? (
                      <div
                        className="pointer-events-none absolute right-5 top-5 z-[3] flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md"
                        style={{ backgroundColor: "var(--precedent-primary)" }}
                        aria-hidden
                      >
                        <CheckOutlined className="!text-[13px] leading-none" />
                      </div>
                    ) : null}
                    <div
                      className="pointer-events-none absolute bottom-3 left-3 z-[2] flex gap-2 opacity-0 transition-[opacity,transform] duration-[160ms] ease-out [transform:translateY(4px)] group-hover:pointer-events-auto group-hover:opacity-100 group-hover:[transform:translateY(0)]"
                      role="presentation"
                    >
                      <button
                        type="button"
                        className={PRECEDENT_ACTION_CHIP}
                        aria-label={`Enlarge preview of slide frame ${idx + 1}`}
                        onClick={() =>
                          setEnlargePreview({
                            id: frameKey,
                            title: `Slide frame ${idx + 1}`,
                            src,
                          })
                        }
                      >
                        <span className={PRECEDENT_ACTION_ICON_WRAP}>
                          <ExpandOutlined className="!block !h-3.5 !w-3.5" />
                        </span>
                        Enlarge
                      </button>
                      <SlideDetailsPopoverChip
                        content={detailsContent}
                        triggerClassName={PRECEDENT_ACTION_CHIP}
                        ariaLabel={`Details for slide frame ${idx + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedSlideFrameKey(frameKey)}
                      className={`relative z-0 block w-full cursor-pointer overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                        isSelected
                          ? "focus-visible:ring-[color:var(--precedent-primary)]"
                          : "focus-visible:ring-gray-500"
                      }`}
                      aria-pressed={isSelected}
                      aria-label={`Slide frame ${idx + 1} (${frameKey}). Click to select this precedent; click again to clear.`}
                    >
                      <div className="aspect-video w-full">
                        <img
                          src={src}
                          alt=""
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => setCreateNewPrecedentIntent(!createNewPrecedentIntent)}
                style={createNewPrecedentIntent ? selectedAccentStyle : undefined}
                className={clsx(
                  "group relative w-full overflow-hidden rounded-md border-2 bg-gray-50 text-center transition-[box-shadow,border-color] duration-[160ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                  createNewPrecedentIntent
                    ? "border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-[color:var(--precedent-primary)]"
                    : "border-gray-200 shadow-sm hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-gray-500"
                )}
                aria-pressed={createNewPrecedentIntent}
                aria-label="Choose Create New precedent; clears any selected slide frame. Click Next to continue when using this path."
              >
                {createNewPrecedentIntent ? (
                  <>
                    <div
                      className="pointer-events-none absolute inset-0 z-[1] rounded-md"
                      style={{
                        backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
                      }}
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute right-5 top-5 z-[3] flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md"
                      style={{ backgroundColor: "var(--precedent-primary)" }}
                      aria-hidden
                    >
                      <CheckOutlined className="!text-[13px] leading-none" />
                    </div>
                  </>
                ) : (
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                    aria-hidden
                  />
                )}

                <div className="relative z-0 flex aspect-video w-full flex-col items-center justify-center gap-1 px-2">
                  <span className="text-3xl font-light leading-none text-gray-800">+</span>
                  <span className="text-sm font-semibold text-gray-800">Create New</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <SlidePreviewEnlargeModal
        open={enlargePreview != null}
        onClose={() => setEnlargePreview(null)}
        slides={slides}
        activeSlide={enlargePreview}
        onActiveSlideChange={setEnlargePreview}
        headerTitle="Preview Slide"
        primaryActionLabel="Select This Slide"
        onPrimaryAction={() => {
          if (enlargePreview == null) return;
          setSelectedSlideFrameKey(enlargePreview.id as any);
          setEnlargePreview(null);
        }}
      />
    </div>
  );
}
